import { useEffect, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa, faNum } from "../game/cards";
import type { WalletState } from "../gen/bazivan/v1/table_pb";

/**
 * نوارِ کیف — سکه، گوهر، و شمارِ قلم‌های انبار.
 *
 * نشانه‌ها تصویرند و نه اموجی. «🪙» از یونیکد ۱۳ است و روی هر دستگاهی
 * نیست: جایی مربعِ خالی می‌شد و جایی شکلِ دیگری — و همان تصویرهایی که
 * لندینگ استفاده می‌کند این‌جا هم بودند و استفاده نمی‌شدند.
 *
 * عددها را از سرور می‌گیرد و هیچ‌وقت خودش حساب نمی‌کند: جایزه‌ی میز، ورودی و
 * خرید همه سمتِ سرور می‌افتند و این‌جا فقط آخرین عددِ رسیده نشان داده می‌شود.
 * تا رسیدنِ اولین پیام چیزی نشان نمی‌دهد — «۰» برای بازیکنی که هزار سکه دارد
 * بدتر از هیچ است.
 */
export function WalletBar() {
  const wallet = useSignal(client.wallet);
  const inventory = useSignal(client.inventory);
  const delta = useSignal(client.walletDelta);

  if (!wallet) return null;

  return (
    <div className="row wallet" style={{ gap: 12, alignItems: "center" }}>
      <Level state={wallet} />
      <Amount icon="/art/ui/ic-coin.webp" alt="سکه" value={wallet.coins} delta={delta?.deltaCoins} />
      <Amount icon="/art/ui/ic-gem.webp" alt="گوهر" value={wallet.gems} delta={delta?.deltaGems} />
      {inventory.size > 0 && (
        <span className="muted" title="قلم‌هایی که داری">
          {fa(inventory.size)} قلم
        </span>
      )}
    </div>
  );
}

/**
 * نشانِ سطح با نوارِ تجربه.
 *
 * سه عدد از سرور می‌آیند (سطح، درونِ سطح، لازم) و این‌جا هیچ منحنی‌ای
 * حساب نمی‌شود: منحنی روزی عوض می‌شود و کلاینتی که خودش حساب کند، از آن
 * روز عددِ دیگری نشان می‌دهد.
 *
 * سرورِ قدیمی صفر می‌فرستد و نشان اصلاً نمی‌آید — بهتر از «سطحِ ۰».
 */
export function Level({ state }: { state: WalletState }) {
  const [up, setUp] = useState(false);

  useEffect(() => {
    if (!state.leveledUp) return;
    setUp(true);
    const t = setTimeout(() => setUp(false), 2200);
    return () => clearTimeout(t);
  }, [state.leveledUp, state.level]);

  if (state.level <= 0) return null;
  const pct = state.levelNeed > 0
    ? Math.max(0, Math.min(100, Math.round((state.levelXp / state.levelNeed) * 100)))
    : 100;

  return (
    <span className={"lvl" + (up ? " lvlup" : "")}
          title={`${fa(state.levelXp)} از ${fa(state.levelNeed)} تجربه تا سطح بعد`}>
      <b>سطح {fa(state.level)}</b>
      <span className="lvlbar"><span style={{ width: pct + "%" }} /></span>
      {up && <span className="gain">سطح {fa(state.level)}!</span>}
    </span>
  );
}

/**
 * یک عدد با نشانه‌اش، و پرشِ کوتاهِ «۱۸۰+» روی تغییر.
 *
 * دلتا از خودِ پیامِ سرور می‌آید و این‌جا از تفاضلِ دو رندر ساخته نمی‌شود:
 * تفاضل با هر رندرِ دوباره یا هر بازاتصال یک پرشِ دروغین می‌ساخت.
 */
export function Amount({ icon, alt, value, delta }: { icon: string; alt: string; value: bigint; delta?: bigint }) {
  const [flash, setFlash] = useState<bigint | null>(null);

  useEffect(() => {
    if (delta === undefined || delta === 0n) return;
    setFlash(delta);
    const t = setTimeout(() => setFlash(null), 1400);
    return () => clearTimeout(t);
  }, [delta, value]);

  return (
    <span className="row" style={{ gap: 4, position: "relative" }}>
      <img src={icon} alt="" width="96" height="96" decoding="async" title={alt} />
      <b>{faNum(value)}</b>
      {flash !== null && (
        <span className={flash > 0n ? "gain" : "loss"}>
          {flash > 0n ? "+" : "−"}{faNum(flash < 0n ? -flash : flash)}
        </span>
      )}
    </span>
  );
}
